// Implements: F-MCK-014, F-MCK-021, F-VER-004
/**
 * The editor's "Más acciones" (mockup v3 :2221): Duplicar, Transferir propiedad, Exportar (HTML,
 * ZIP, diagram ZIP), Mover a otro ámbito and Papelera.
 *
 * Duplicar and Exportar are for everyone who can read the mockup; Transferir, Mover and Papelera
 * only when the server says the caller may do each (`allowedActionsOf`, the rule the gallery card
 * menu shares): the server refuses the rest anyway, so offering them would only lead to a refusal. Exportar uses the version the canvas shows (`versionId`) before
 * the current one. The transfer dialog is connected to the page's bridge, so the share popover's
 * "Transferir" opens the same one. The page mounts it keyed by the mockup id (checklist 14).
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router";

import type { IconName } from "../../../shared/design-system/icons";
import { useTranslation } from "../../../shared/i18n";
import { buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { Menu } from "../../../shared/ui/molecules/Menu";
import type { MenuEntry } from "../../../shared/ui/molecules/Menu";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import type { VersionDownload } from "../../../types/api";
import { useMockupActions } from "../hooks/useMockupActions";
import { useMockupDetail } from "../hooks/useMockupDetail";
import type { MoreActionsBridge } from "../hooks/useMoreActionsBridge";
import { allowedActionsOf } from "../model/allowedActions";
import { ScopeChangeContainer } from "./ScopeChangeContainer";
import { TransferOwnershipDialog } from "./TransferOwnershipDialog";
import { useDuplicateMockup } from "./useDuplicateMockup";
import { useExportMockupVersion } from "./useExportMockupVersion";

export interface MockupMoreActionsContainerProps {
  readonly mockupId: string;
  /** The version the canvas shows; Exportar uses it before the current one. */
  readonly versionId?: string | null | undefined;
  /** Where the container connects its transfer dialog, so the share popover can open it. */
  readonly bridge?: MoreActionsBridge | undefined;
}

/** The gallery's route (`ROUTE_PATHS.gallery`); a trashed mockup has nothing left to show here. */
const GALLERY_PATH = "/";

type Open = "transfer" | "move" | "trash" | null;

export function MockupMoreActionsContainer({
  mockupId,
  versionId = null,
  bridge,
}: MockupMoreActionsContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const { push } = useToast();
  const navigate = useNavigate();
  const detail = useMockupDetail(mockupId).data;
  const duplicate = useDuplicateMockup();
  const exportVersion = useExportMockupVersion();
  const { trash } = useMockupActions();
  const [open, setOpen] = useState<Open>(null);
  const [trashing, setTrashing] = useState(false);
  const trashSent = useRef(false);

  useEffect(() => {
    bridge?.connect(() => {
      setOpen("transfer");
    });
    // Unmounted, the share popover's "Transferir" must open nothing here.
    return () => {
      bridge?.connect(() => undefined);
    };
  }, [bridge]);

  if (detail === undefined) return null;
  const may = allowedActionsOf(detail);
  const label = t("mockups.more.label");

  const item = (id: string, text: string, icon: IconName, onSelect: () => void): MenuEntry => ({
    kind: "item",
    id,
    label: text,
    icon,
    onSelect,
  });
  const exportItem = (kind: VersionDownload, text: string): MenuEntry =>
    item(`export-${kind}`, text, kind === "diagram" ? "diagram" : "download", () => {
      void exportVersion(mockupId, kind, versionId);
    });

  const entries: readonly MenuEntry[] = [
    item("duplicate", t("mockups.menu.duplicate"), "copy", () => {
      void duplicate(mockupId);
    }),
    ...(may.transfer
      ? [
          item("transfer", t("mockups.menu.transfer"), "users", () => {
            setOpen("transfer");
          }),
        ]
      : []),
    { kind: "separator", id: "export-separator" },
    { kind: "label", id: "export", label: t("mockups.menu.exportGroup") },
    exportItem("html", t("mockups.menu.exportHtml")),
    exportItem("zip", t("mockups.menu.exportZip")),
    exportItem("diagram", t("mockups.menu.exportDiagram")),
    ...(may.move || may.trash ? ([{ kind: "separator", id: "owner-separator" }] satisfies MenuEntry[]) : []),
    ...(may.move
      ? [
          item("move", t("gallery.card.move"), "folder", () => {
            setOpen("move");
          }),
        ]
      : []),
    ...(may.trash
      ? ([
          {
            kind: "item",
            id: "trash",
            label: t("gallery.card.trash"),
            icon: "trash",
            danger: true,
            onSelect: () => {
              setOpen("trash");
            },
          },
        ] satisfies MenuEntry[])
      : []),
  ];

  const close = (): void => {
    setOpen(null);
  };

  const confirmTrash = async (): Promise<void> => {
    if (trashSent.current) return;
    trashSent.current = true;
    setTrashing(true);
    const done = await trash(mockupId);
    trashSent.current = false;
    setTrashing(false);
    if (done) {
      push({ tone: "success", message: t("gallery.card.trashed", { name: detail.name }) });
      void navigate(GALLERY_PATH);
    } else {
      setOpen(null);
      push({ tone: "danger", message: t("gallery.card.trashFailed") });
    }
  };

  return (
    <>
      <Menu
        label={label}
        align="end"
        entries={entries}
        renderTrigger={(trigger) => (
          <button type="button" {...trigger} className={buttonClassName("ghost", "sm", true)} aria-label={label}>
            <Icon name="more" size="sm" />
          </button>
        )}
      />
      {open === "transfer" && may.transfer ? (
        <TransferOwnershipDialog
          key={mockupId}
          mockupId={mockupId}
          mockupName={detail.name}
          organizationId={detail.organization_id}
          ownerId={detail.owner_id}
          personal={detail.scope_kind === "personal"}
          onClose={close}
        />
      ) : null}
      {open === "move" ? (
        <Dialog open title={t("gallery.move.title")} onClose={close}>
          <ScopeChangeContainer key={mockupId} mockupId={mockupId} />
        </Dialog>
      ) : null}
      <ConfirmDialog
        open={open === "trash"}
        title={t("mockups.more.trashTitle", { name: detail.name })}
        body={t("mockups.more.trashBody")}
        confirmLabel={t("gallery.card.trash")}
        cancelLabel={t("common.action.cancel")}
        confirmLoading={trashing}
        onConfirm={() => {
          void confirmTrash();
        }}
        onCancel={close}
      />
    </>
  );
}
